import { IdContato, IdInbox } from "../common-types"
import { GenericServiceBase } from "../generic-service"
import { MetaUploadMime } from "../meta-uploads/meta-uploads.service"
import { StringOfMax1024Chars, StringOfMax60Chars } from "./strings"
import { MetaNamedParametersBody, MetaParameterlessBody, MetaPositionalParametersBody } from "./template-body"
import { MetaMediaHeader_CRUD, MetaNamedParameterTextHeader_CRUD, MetaParameterlessTextHeader_CRUD, MetaPositionalParameterTextHeader_CRUD, MetaTemplateHeader_CRUD } from "./template-headers"

export type MetaTemplateParameterFormat = "POSITIONAL" | "NAMED"
export type MetaHeaderType              = "TEXT_PARAMETERLESS" | "TEXT_NAMED_PARAMETER" | "TEXT_POSITIONAL_PARAMETER" | "MEDIA_PDF" | "MEDIA_PNG_JPG_JPEG" | "MEDIA_MP4" | "LOCATION"
export type MetaBodyType                = "TEXT_PARAMETERLESS" | "TEXT_NAMED_PARAMETERS" | "TEXT_POSITIONAL_PARAMETERS"
export type MetaFooterType              = "TEXT_PARAMETERLESS"
export type MetaTemplateCategory        = "MARKETING" | "UTILITY" | "AUTHENTICATION"
export type MetaTemplateStatus          = "APPROVED" | "ARCHIVED" | "DELETED" | "DISABLED" | "FLAGGED" | "IN_APPEAL" | "LIMIT_EXCEEDED" | "LOCKED" | "PAUSED" | "PENDING" | "PENDING_DELETION" | "REINSTATED" | "REJECTED"
export type MetaTemplateRejectionReason = "ABUSIVE_CONTENT" | "CATEGORY_NOT_AVAILABLE" | "INCORRECT_CATEGORY" | "INVALID_FORMAT" | "NONE" | "PROMOTIONAL" | "SCAM" | "TAG_CONTENT_MISMATCH"

export function getHeaderType(type: 'text'|'file', variables?: false|'named'|'positional', mime?: MetaUploadMime): MetaHeaderType {
    if (type == 'text') {
        switch (variables) {
            case 'named':
                return "TEXT_NAMED_PARAMETER";
            case 'positional':
                return "TEXT_POSITIONAL_PARAMETER";
            case false:
            default:
                return "TEXT_PARAMETERLESS";
        }
    } else {
        switch (mime) {
            case 'JPEG':
            case 'JPG':
            case 'PNG':
                return "MEDIA_PNG_JPG_JPEG";
            case 'MP4':
                return "MEDIA_MP4";
            case 'PDF':
                return "MEDIA_PDF";
            default:
                throw new Error(`[getHeaderType] Combinação inválida: ${type}, ${variables}, ${mime}`)
        }
    }
}

export function getHeaderParser(type: MetaHeaderType, options?: { text?: string, headerHandle?: string, positionalExample?: string, namedExample?: string, namedName?: string }): MetaTemplateHeader_CRUD {
    switch (type) {
        case "TEXT_PARAMETERLESS"       : return new MetaParameterlessTextHeader_CRUD(options?.text as StringOfMax60Chars);
        case "TEXT_NAMED_PARAMETER"     : return new MetaNamedParameterTextHeader_CRUD(options?.text as StringOfMax60Chars, { example: options?.namedExample ?? '', param_name: options?.namedName ?? '' });
        case "TEXT_POSITIONAL_PARAMETER": return new MetaPositionalParameterTextHeader_CRUD(options?.text as StringOfMax60Chars, options?.positionalExample);
        case "MEDIA_PDF"                : return new MetaMediaHeader_CRUD("document", options?.headerHandle ?? '');
        case "MEDIA_PNG_JPG_JPEG"       : return new MetaMediaHeader_CRUD("image", options?.headerHandle ?? '');
        case "MEDIA_MP4"                : return new MetaMediaHeader_CRUD("video", options?.headerHandle ?? '');
        case "LOCATION"                 : throw new Error("Location header unimplemented");
    }
}

export function getBodyType(variables: false|'named'|'positional'): MetaBodyType {
    switch (variables) {
        case false:
            return "TEXT_PARAMETERLESS";
        case 'named':
            return "TEXT_NAMED_PARAMETERS";
        case 'positional':
            return "TEXT_POSITIONAL_PARAMETERS";
    }
}

export function getBodyParser(type: MetaBodyType, options?: { text: string, positionalExamples?: string[], namedExamples?: { param_name: string, example: string }[] }) {
    switch (type) {
        case "TEXT_PARAMETERLESS"        : return new MetaParameterlessBody(options?.text as StringOfMax1024Chars)
        case "TEXT_NAMED_PARAMETERS"     : return new MetaNamedParametersBody(options?.text as StringOfMax1024Chars, options?.namedExamples)
        case "TEXT_POSITIONAL_PARAMETERS": return new MetaPositionalParametersBody(options?.text as StringOfMax1024Chars, options?.positionalExamples)
    }
}

export interface IMetaTemplate {
    id                 : string;
    idMeta            ?: string | null;
    idMetaUpload      ?: string | null;
    wabaId            ?: string;
    idInbox            : string;
    displayName        : string;
    metaName           : string;
    language           : string;
    parameterFormat    : MetaTemplateParameterFormat
    headerType        ?: MetaHeaderType | null;
    headerJson        ?: string | null;
    bodyType           : MetaBodyType;
    bodyJson           : string;
    footerType        ?: MetaFooterType | null;
    footerJson        ?: string | null;
    buttonsJson       ?: string | null;
    /** Categoria APROVADA pela Meta. */
    category           : MetaTemplateCategory;
    /**
     * Categoria que o usuário pediu. Diferente de `category` = a Meta reclassificou
     * (o envio é cobrado pela aprovada). Nula = desconhecida, igual a `category`.
     */
    categoriaSolicitada?: MetaTemplateCategory | null;
    /** Calculado pelo server: `categoriaSolicitada` presente e diferente de `category`. */
    reclassificado    ?: boolean;
    status            ?: MetaTemplateStatus | null;
    rejectionReason   ?: MetaTemplateRejectionReason | null;
    reasonInfo        ?: string | null;
    recommendationInfo?: string | null;
    del               ?: boolean;

    /**
     * Arquivo do cabeçalho de mídia.
     *
     * O nome é `metaUpload`, MINÚSCULO — é assim que `ResponseMetaTemplateDto`
     * serializa, e era declarado aqui como `MetaUpload`. Campo que não existe no
     * payload lê `undefined` em silêncio: a pré-visualização ficava sem URL e caía
     * sempre no cartão de "Documento", mesmo para template com imagem ou vídeo.
     *
     * `Storage` é a nossa cópia re-hospedada (maiúsculo mesmo, é o nome da relação
     * no Prisma); `s3FileAddress` é o endereço direto, usado quando não há Storage.
     */
    metaUpload?: {
        id               : string,
        mime             : MetaUploadMime,
        s3FileAddress   ?: string | null,
        originalFileName : string,
        fileSizeInBytes  : number,
        fileHandle      ?: string | null,
        Storage         ?: {
            id      : string,
            path    : string,
            filename: string,
            type    : string,
        } | null
    } | null
}

/** Nome excluído há menos de 30 dias — a Meta ainda não deixa reutilizá-lo. */
export interface INomeReservado {
    metaName   : string;
    language   : string;
    displayName: string | null;
    excluidoEm : string;
    liberaEm   : string;
}

export interface IResumoWaba {
    wabaId          : string;
    /** Templates na WABA — cada idioma conta como um. */
    usados          : number;
    /** 250 sem verificação do negócio, 6.000 com. */
    limite          : number;
    /** `LOCAL` = a Meta não respondeu; só conta os templates conhecidos pelo Whiz. */
    fonteContagem   : 'META' | 'LOCAL';
    negocioVerificado: boolean;
    nomesReservados : INomeReservado[];
}

/** Mesmo saneamento do servidor (`MetaTemplatesService.create`) do nome de exibição para o nome técnico. */
export const nomeTecnicoDoTemplate = (displayName: string): string =>
    displayName.trim().toLowerCase().replaceAll(/\s/g, '_').replaceAll(/[^a-z0-9_]/g, '').slice(0, 512);

export interface ICreateMetaTemplate extends Pick<IMetaTemplate, 'idInbox'|'idMetaUpload'|'displayName'|'language'|'parameterFormat'|'category'|'headerType'|'headerJson'|'bodyType'|'bodyJson'|'footerType'|'footerJson'|'buttonsJson'> {
    metaName?: string
}

export type IMetaParams = IMetaTemplateNoParams | IMetaTemplatePositionalParams | IMetaTemplateNamedParams

export interface IMetaTemplatePositionalParams {
    header: string[],
    body: string[],
}

export interface IMetaTemplateNamedParams {
    header: [paramName: string, paramValue: string][],
    body: [paramName: string, paramValue: string][]
}

export interface IMetaTemplateNoParams {
    replace: null
}

export class MetaTemplatesService extends GenericServiceBase {
    constructor() { super('meta-templates') }

    async send(metaWppTemplateId: string, idContato: IdContato, toReplace: IMetaParams = { replace: null }) {
        // todo: o que raios essa coisa me retorna?
        return await this.f.post(`/send/${metaWppTemplateId}/to/${idContato}`, toReplace)
    }

    async create(toCreate: ICreateMetaTemplate): Promise<IMetaTemplate> {
        return await this.f.post(`/`, toCreate) as IMetaTemplate;
    }

    async findAllByInbox(idInbox: IdInbox) {
        return await this.f.get(`/inbox/${idInbox}`) as IMetaTemplate[];
    }

    async findAll() {
        return await this.f.get(``) as IMetaTemplate[];
    }

    /** Ocupação de templates da WABA do inbox (usados / limite) e nomes reservados pós-exclusão. */
    async resumoDaWaba(idInbox: IdInbox | string): Promise<IResumoWaba> {
        return await this.f.get(`/inbox/${idInbox}/resumo-waba`) as IResumoWaba;
    }

    async findOne(id: string) {
        return await this.f.get(`/${id}`) as IMetaTemplate|null;
    }

    async update(id: string, toUpdate: Partial<IMetaTemplate>) {
        return await this.f.patch(`/${id}`, toUpdate) as IMetaTemplate;
    }

    async refreshStatus(idTemplate: string): Promise<IMetaTemplate> {
        return await this.f.get(`/refresh-status/${idTemplate}`) as IMetaTemplate;
    }

    async remove(id: string) {
        return await this.f.delete(`/${id}`);
    }

    static getStatusMeta(status?: MetaTemplateStatus | null) {
        switch (status) {
            case 'APPROVED'        : return 'Template aprovado'
            case 'ARCHIVED'        : return 'Template arquivado'
            case 'DELETED'         : return 'Template deletado'
            case 'DISABLED'        : return 'Template desabilitado'
            case 'FLAGGED'         : return 'Template marcado (flagged)'
            case 'IN_APPEAL'       : return 'Revisão solicitada'
            case 'LIMIT_EXCEEDED'  : return 'Template desabilitado por exceder o limite'
            case 'LOCKED'          : return 'Template trancado'
            case 'PAUSED'          : return 'Template pausado'
            case 'PENDING'         : return 'Aprovação pendente'
            case 'PENDING_DELETION': return 'Deleção pendente'
            case 'REINSTATED'      : return 'Template restaurado'
            case 'REJECTED'        : return 'Template rejeitado'
                default           : return 'Status desconhecido'
        }
    }

    static getTemplateTypeIconMeta(headerType?: MetaHeaderType | null) {
        switch (headerType) {
            case "MEDIA_PDF":
                return "ri-file-pdf-2-line";
            case "MEDIA_PNG_JPG_JPEG":
                return "ri-image-line";
            case "MEDIA_MP4":
                return "ri-youtube-line";
            case "LOCATION":
                return "ri-map-pin-line";
            case "TEXT_PARAMETERLESS":
            case "TEXT_NAMED_PARAMETER":
            case "TEXT_POSITIONAL_PARAMETER":
            default:
                return "ri-text";
        }
    }

    static getIconColorClassMeta(headerType?: MetaHeaderType | null) {
        switch (headerType) {
            case "MEDIA_PDF":
            case "MEDIA_PNG_JPG_JPEG":
            case "MEDIA_MP4":
                return "bg-warning-subtle text-warning";
            case "LOCATION":
                return "bg-success-subtle text-success";
            case "TEXT_PARAMETERLESS":
            case "TEXT_NAMED_PARAMETER":
            case "TEXT_POSITIONAL_PARAMETER":
            default:
                return "bg-primary-subtle text-primary";
        }
    }

    static getTemplateTextMeta(headerType?: MetaHeaderType | null) {
        switch (headerType) {
            case "MEDIA_PDF"                : return 'Template PDF'
            case "MEDIA_PNG_JPG_JPEG"       : return 'Template de Imagem'
            case "MEDIA_MP4"                : return 'Template de Vídeo'
            case "LOCATION"                 : return 'Template de Localização'
            case "TEXT_PARAMETERLESS"       :
            case "TEXT_NAMED_PARAMETER"     :
            case "TEXT_POSITIONAL_PARAMETER":
            default:
                return 'Template de Texto'
        }
    }

    static getTemplateStatusIconMeta(headerStatus?: MetaTemplateStatus | null ) {
        switch (headerStatus) {
            case "APPROVED"        : return 'ri-check-fill'
            case "ARCHIVED"        : return 'ri-archive-2-fill'
            case "DELETED"         : return 'ri-delete-bin-7-fill'
            case "DISABLED"        : return 'ri-prohibited-line'
            case "FLAGGED"         : return 'ri-flag-fill'
            case "IN_APPEAL"       : return 'ri-time-line'
            case "LIMIT_EXCEEDED"  : return 'ri-zzz-line'
            case "LOCKED"          : return 'ri-lock-2-line'
            case "PAUSED"          : return 'ri-pause-fill'
            case "PENDING"         : return 'ri-time-line'
            case "PENDING_DELETION": return 'ri-time-line'
            case "REINSTATED"      : return 'ri-restart-line'
            case "REJECTED"        : return 'ri-close-fill'
                 default           : return 'ri-question-mark'
        }
    }
}
